import { Component } from 'react';
import SakuraOrbit from './SakuraOrbit';
import { DataRPC } from '../../rpc-client/DataRPC';
import { s3 } from '../../config/constants';

const rpc = new DataRPC();
const PAGE_SIZE = 100;

function parsePageParam(search) {
    const p = parseInt(new URLSearchParams(search).get('page') || '1', 10);
    return isNaN(p) || p < 1 ? 1 : p;
}

class AlbumDetail extends Component {
    state = {
        album:          null,
        pictures:       [],
        total:          0,
        page:           1,
        loadingInit:    true,
        loadingPage:    false,
        lbIndex:        null,
        // teams
        teams:          [],
        teamsLoading:   true,
        // tagging
        tagMode:        false,
        selectedTeamId: null,
        selectedPics:   new Set(),
        taggedPics:     new Map(),
        tagging:        false,
        tagSuccess:     0,
        // toast
        toast:          null,
    };

    componentDidMount() {
        const id   = this.props.match.params.id;
        const page = parsePageParam(this.props.location.search);

        Promise.all([
            rpc.fetchRawData('GetAlbum1',       { id }),
            rpc.fetchRawData('GetPictures',     { album_id: id, page, limit: PAGE_SIZE }),
            rpc.fetchRawData('GetPictureTeams', { album_id: id }),
        ]).then(([albumRes, picRes, tagRes]) => {
            const pics  = picRes?.data  ?? [];
            const total = picRes?.total ?? 0;
            const tags  = Array.isArray(tagRes) ? tagRes : [];
            const taggedPics = new Map();
            tags.forEach(({ picture_id, team_id }) => {
                const key = String(picture_id);
                const s   = taggedPics.get(key) || new Set();
                s.add(String(team_id));
                taggedPics.set(key, s);
            });
            this.setState({ album: albumRes || null, pictures: pics, total, page, taggedPics, loadingInit: false });
        }).catch(() => this.setState({ loadingInit: false }));

        rpc.fetchRawData('GetTeams', { page: 1, limit: 200 })
            .then(res => this.setState({ teams: res?.data ?? [], teamsLoading: false }))
            .catch(() => this.setState({ teamsLoading: false }));

        document.addEventListener('keydown', this.onKey);
    }

    componentDidUpdate(prevProps) {
        if (prevProps.location.search === this.props.location.search) return;
        const newPage = parsePageParam(this.props.location.search);
        if (newPage === this.state.page) return;
        const id = this.props.match.params.id;
        this.setState({ loadingPage: true, lbIndex: null, selectedPics: new Set(), tagSuccess: 0 });
        rpc.fetchRawData('GetPictures', { album_id: id, page: newPage, limit: PAGE_SIZE })
            .then(res => {
                this.setState({ pictures: res?.data ?? [], total: res?.total ?? 0, page: newPage, loadingPage: false });
                window.scrollTo({ top: 0, behavior: 'smooth' });
            })
            .catch(() => this.setState({ loadingPage: false }));
    }

    componentWillUnmount() {
        document.removeEventListener('keydown', this.onKey);
    }

    goToPage = (p) => {
        const totalPages = Math.ceil(this.state.total / PAGE_SIZE);
        if (p < 1 || p > totalPages) return;
        this.props.history.push({ search: p === 1 ? '' : `?page=${p}` });
    };

    onKey = (e) => {
        const { lbIndex, pictures } = this.state;
        if (lbIndex === null) return;
        if (e.key === 'ArrowRight') this.setState({ lbIndex: (lbIndex + 1) % pictures.length });
        if (e.key === 'ArrowLeft')  this.setState({ lbIndex: (lbIndex - 1 + pictures.length) % pictures.length });
        if (e.key === 'Escape')     this.setState({ lbIndex: null });
    };

    openLb  = (i) => this.setState({ lbIndex: i });
    closeLb = ()  => this.setState({ lbIndex: null });
    prevLb  = (e) => { e.stopPropagation(); const { lbIndex, pictures } = this.state; this.setState({ lbIndex: (lbIndex - 1 + pictures.length) % pictures.length }); };
    nextLb  = (e) => { e.stopPropagation(); const { lbIndex, pictures } = this.state; this.setState({ lbIndex: (lbIndex + 1) % pictures.length }); };

    toggleTagMode   = () => this.setState(s => ({ tagMode: !s.tagMode, selectedPics: new Set(), tagSuccess: 0 }));
    togglePicSelect = (id) => this.setState(s => {
        const key  = String(id);
        const next = new Set(s.selectedPics);
        if (next.has(key)) next.delete(key); else next.add(key);
        return { selectedPics: next };
    });
    selectAll      = () => this.setState(s => ({ selectedPics: new Set(s.pictures.map(p => String(p.id))) }));
    clearSelection = () => this.setState({ selectedPics: new Set() });

    tagSelected = async () => {
        const { selectedPics, selectedTeamId } = this.state;
        if (!selectedTeamId || selectedPics.size === 0) return;
        this.setState({ tagging: true, tagSuccess: 0 });
        const ids = [...selectedPics];
        const results = await Promise.allSettled(
            ids.map(picId => rpc.fetchRawData('TagPictureTeam', { picture_id: picId, team_id: selectedTeamId }))
        );
        const succeeded = results.filter(r => r.status === 'fulfilled').length;
        this.setState(s => {
            const next = new Map(s.taggedPics);
            ids.forEach(picId => {
                const key   = String(picId);
                const teams = new Set(next.get(key) || []);
                teams.add(String(selectedTeamId));
                next.set(key, teams);
            });
            return { taggedPics: next, tagging: false, selectedPics: new Set(), tagSuccess: succeeded };
        });
    };

    handlePicClick = (pic, index) => {
        if (this.state.tagMode) {
            const { selectedTeamId, taggedPics } = this.state;
            if (selectedTeamId && taggedPics.get(String(pic.id))?.has(String(selectedTeamId))) return;
            this.togglePicSelect(pic.id);
        } else {
            this.openLb(index);
        }
    };

    showToast = (msg) => {
        this.setState({ toast: msg });
        setTimeout(() => this.setState({ toast: null }), 2200);
    };

    downloadFile = async (url, filename) => {
        try {
            const res  = await fetch(url, { mode: 'cors' });
            const blob = await res.blob();
            const a    = document.createElement('a');
            a.href     = URL.createObjectURL(blob);
            a.download = filename;
            a.click();
            setTimeout(() => URL.revokeObjectURL(a.href), 10000);
        } catch {
            window.open(url, '_blank');
        }
    };

    downloadImage = (pic) => {
        const url      = pic.image_url || pic.thumbnail_url;
        const filename = (pic.caption || `photo-${pic.id}`).replace(/[^\w-]/g, '_') + '.jpg';
        this.downloadFile(url, filename);
    };

    downloadAlbum = () => {
        const { album, pictures } = this.state;
        const url      = album?.cover_image_url || pictures[0]?.image_url || pictures[0]?.thumbnail_url;
        if (!url) return;
        const filename = (album?.title || 'album').replace(/[^\w-]/g, '_') + '_cover.jpg';
        this.downloadFile(url, filename);
    };

    shareImage = async (pic) => {
        const url = pic.image_url || pic.thumbnail_url;
        if (navigator.share) {
            try { await navigator.share({ title: pic.caption || 'Ảnh Yosakoi', url }); } catch {}
        } else {
            try { await navigator.clipboard.writeText(url); this.showToast('Đã sao chép link ảnh!'); } catch {}
        }
    };

    shareAlbum = async () => {
        const url = window.location.href;
        if (navigator.share) {
            try { await navigator.share({ title: this.state.album?.title || 'Album Yosakoi', url }); } catch {}
        } else {
            try { await navigator.clipboard.writeText(url); this.showToast('Đã sao chép link album!'); } catch {}
        }
    };

    renderPagination(totalPages) {
        const { page } = this.state;
        if (totalPages <= 1) return null;

        const pages = [];
        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
        } else {
            pages.push(1);
            if (page > 3) pages.push('...');
            for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) pages.push(i);
            if (page < totalPages - 2) pages.push('...');
            pages.push(totalPages);
        }

        return (
            <div className="alb-pagination">
                <button className="alb-pg-btn alb-pg-prev" onClick={() => this.goToPage(page - 1)} disabled={page === 1}>‹</button>
                {pages.map((p, i) =>
                    p === '...'
                        ? <span key={`e${i}`} className="alb-pg-ellipsis">…</span>
                        : <button key={p} className={`alb-pg-btn${p === page ? ' alb-pg-btn--active' : ''}`} onClick={() => this.goToPage(p)}>{p}</button>
                )}
                <button className="alb-pg-btn alb-pg-next" onClick={() => this.goToPage(page + 1)} disabled={page === totalPages}>›</button>
            </div>
        );
    }

    render() {
        const {
            album, pictures, total, page, loadingInit, loadingPage, lbIndex,
            teams, teamsLoading,
            tagMode, selectedTeamId, selectedPics, taggedPics, tagging, tagSuccess,
            toast,
        } = this.state;

        const lbPic      = lbIndex !== null ? pictures[lbIndex] : null;
        const totalPages = Math.ceil(total / PAGE_SIZE);
        const teamsById  = Object.fromEntries(teams.map(t => [String(t.id), t]));
        const selectedTeamName = teamsById[String(selectedTeamId)]?.name ?? '';
        const pageStart  = total > 0 ? (page - 1) * PAGE_SIZE + 1 : 0;
        const pageEnd    = Math.min(page * PAGE_SIZE, total);

        return (
            <div className="alb-detail-pg">

                {/* ── Tag widget (fixed side panel) ── */}
                {!loadingInit && pictures.length > 0 && (
                    <div className="alb-tag-widget">
                        <select
                            className="alb-tag-select"
                            value={selectedTeamId || ''}
                            onChange={e => this.setState({ selectedTeamId: e.target.value || null, tagSuccess: 0 })}
                            disabled={teamsLoading}
                        >
                            <option value="">{teamsLoading ? 'Đang tải...' : '— Chọn đội —'}</option>
                            {teams.map(t => (
                                <option key={t.id} value={t.id}>{t.name}</option>
                            ))}
                        </select>

                        <button
                            className={`alb-tag-mode-btn${tagMode ? ' active' : ''}`}
                            onClick={this.toggleTagMode}
                            disabled={!selectedTeamId}
                            title={!selectedTeamId ? 'Chọn đội trước' : ''}
                        >
                            {tagMode ? '✕ Thoát tag mode' : '🏷 Bật tag mode'}
                        </button>

                        {tagMode && (
                            <div className="alb-tag-actions">
                                <div className="alb-tag-hint">
                                    Click ảnh để tag cho <em>{selectedTeamName}</em>
                                </div>
                                <div className="alb-tag-count">{selectedPics.size} ảnh đã chọn</div>
                                <div className="alb-tag-btn-row">
                                    <button className="alb-tag-btn alb-tag-btn--all" onClick={this.selectAll}>
                                        Chọn tất cả ({pictures.length})
                                    </button>
                                    <button className="alb-tag-btn alb-tag-btn--clear" onClick={this.clearSelection} disabled={selectedPics.size === 0}>
                                        Xóa
                                    </button>
                                </div>
                                <button
                                    className={`alb-tag-btn alb-tag-btn--submit${tagging ? ' loading' : ''}`}
                                    onClick={this.tagSelected}
                                    disabled={selectedPics.size === 0 || tagging}
                                >
                                    {tagging
                                        ? <><span className="alb-spinner alb-spinner--sm" /> Đang tag...</>
                                        : `Tag ${selectedPics.size} ảnh →`}
                                </button>
                                {tagSuccess > 0 && (
                                    <div className="alb-tag-success">✓ Đã tag {tagSuccess} ảnh</div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* ── Page header ── */}
                <div className="page-header alb-detail-header">
                    <div className="page-header-overlay" />
                    <div className="container page-header-body">
                        <div className="row">
                            <div className="col-12">
                                <div className="entry-meta-date">BỘ SƯU TẬP ẢNH</div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="sep-gold-rule" style={{margin: 0}} />

                {loadingInit ? (
                    <SakuraOrbit label="Đang tải album" />
                ) : !album ? (
                    <div className="alb-detail-loading">Không tìm thấy album.</div>
                ) : (
                    <div className="alb-detail-body">
                        <div className="container">

                            {/* Meta bar */}
                            <div className="alb-meta-bar">
                                {album.cameraman_name && (
                                    <div className="alb-cam-card">
                                        <div className="alb-cam-avatar">
                                            {album.cameraman_avatar_url
                                                ? <img src={s3(album.cameraman_avatar_url)} alt={album.cameraman_name} />
                                                : <span>{album.cameraman_name.charAt(0)}</span>
                                            }
                                        </div>
                                        <div className="alb-cam-info">
                                            <div className="alb-cam-label">Nhiếp ảnh gia</div>
                                            <div className="alb-cam-name">{album.cameraman_name}</div>
                                            {album.cameraman_style && <div className="alb-cam-style">{album.cameraman_style}</div>}
                                            {album.cameraman_introduction && <div className="alb-cam-intro">{album.cameraman_introduction}</div>}
                                            <div className="alb-cam-links">
                                                {album.cameraman_facebook_url && <a href={album.cameraman_facebook_url} target="_blank" rel="noopener" className="alb-cam-link alb-cam-link--fb">Facebook</a>}
                                                {album.cameraman_instagram_url && <a href={album.cameraman_instagram_url} target="_blank" rel="noopener" className="alb-cam-link alb-cam-link--ig">Instagram</a>}
                                                {album.cameraman_email && <a href={`mailto:${album.cameraman_email}`} className="alb-cam-link">Email</a>}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className="alb-info-grid">
                                    <div className="alb-info-grid-title">Thông tin album</div>
                                    <div className="alb-meta-item">
                                        <span className="alb-meta-label">Tên album</span>
                                        <span className="alb-meta-val">{album.title}</span>
                                    </div>
                                    {album.festival_name && (
                                        <div className="alb-meta-item">
                                            <span className="alb-meta-label">Sự kiện</span>
                                            <span className="alb-meta-val">{album.festival_name}</span>
                                        </div>
                                    )}
                                    {album.take_in_year && (
                                        <div className="alb-meta-item">
                                            <span className="alb-meta-label">Năm</span>
                                            <span className="alb-meta-val">{album.take_in_year}</span>
                                        </div>
                                    )}
                                    {total > 0 && (
                                        <div className="alb-meta-item">
                                            <span className="alb-meta-label">Số ảnh</span>
                                            <span className="alb-meta-val">{total}</span>
                                        </div>
                                    )}
                                    {album.description && (
                                        <div className="alb-meta-item alb-meta-desc">
                                            <span className="alb-meta-label">Mô tả</span>
                                            <span className="alb-meta-val">{album.description}</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Album actions */}
                            <div className="alb-album-actions">
                                <button className="alb-action-btn" onClick={this.shareAlbum}>
                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
                                    Chia sẻ album
                                </button>
                            </div>

                            {/* Section title */}
                            <div className="entry-title" style={{marginTop: '40px'}}>
                                <p>BỘ SƯU TẬP</p>
                                <h2>
                                    TẤT CẢ HÌNH ẢNH
                                    {total > 0 && (
                                        <span className="alb-count-pill">{pageStart}–{pageEnd} / {total}</span>
                                    )}
                                </h2>
                            </div>

                            {pictures.length === 0 ? (
                                <div className="alb-no-pics">
                                    <div style={{fontSize: '48px', marginBottom: '12px'}}>桜</div>
                                    <p>Chưa có hình ảnh trong album này.</p>
                                </div>
                            ) : (
                                <>
                                    {this.renderPagination(totalPages)}

                                    <div
                                        className={`alb-pics-grid${tagMode ? ' alb-tag-mode-on' : ''}${loadingPage ? ' alb-pics-grid--loading' : ''}`}
                                    >
                                        {pictures.map((pic, i) => {
                                            const isSelected      = selectedPics.has(String(pic.id));
                                            const picTeamIds      = taggedPics.has(String(pic.id)) ? [...taggedPics.get(String(pic.id))] : [];
                                            const isLockedForTeam = tagMode && selectedTeamId && picTeamIds.includes(String(selectedTeamId));
                                            return (
                                                <div
                                                    key={pic.id}
                                                    className={`alb-pic-item${isSelected ? ' alb-pic-selected' : ''}${isLockedForTeam ? ' alb-pic-locked' : ''}`}
                                                    onClick={() => this.handlePicClick(pic, i)}
                                                >
                                                    <img
                                                        src={pic.thumbnail_url || pic.image_url}
                                                        alt={pic.caption || ''}
                                                        loading="lazy"
                                                    />
                                                    <div className="alb-pic-overlay">
                                                        {tagMode
                                                            ? isLockedForTeam
                                                                ? <span className="alb-pic-check alb-pic-check--locked">✓</span>
                                                                : <span className={`alb-pic-check${isSelected ? ' alb-pic-check--on' : ''}`}>{isSelected ? '✓' : '○'}</span>
                                                            : <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/><path d="M11 8v6M8 11h6"/></svg>
                                                        }
                                                    </div>
                                                    {picTeamIds.length > 0 && (
                                                        <div className="alb-pic-team-badges">
                                                            {picTeamIds.slice(0, 3).map(tid => {
                                                                const team = teamsById[tid];
                                                                return team ? (
                                                                    <div key={tid} className="alb-pic-team-badge" title={team.name}>
                                                                        {team.avatar_url
                                                                            ? <img src={s3(team.avatar_url)} alt={team.name} />
                                                                            : <span>{team.name.charAt(0).toUpperCase()}</span>
                                                                        }
                                                                    </div>
                                                                ) : null;
                                                            })}
                                                            {picTeamIds.length > 3 && (
                                                                <div className="alb-pic-team-badge alb-pic-team-badge--more">+{picTeamIds.length - 3}</div>
                                                            )}
                                                        </div>
                                                    )}
                                                    {pic.caption && <div className="alb-pic-caption">{pic.caption}</div>}
                                                </div>
                                            );
                                        })}
                                    </div>

                                    {this.renderPagination(totalPages)}
                                    {totalPages > 1 && (
                                        <div className="alb-pg-info">Trang {page} / {totalPages} · {total} ảnh</div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                )}

                <div className="sep-gold-rule" style={{margin: 0}} />

                {/* Lightbox */}
                <div className={`gallery-lightbox${lbIndex !== null ? ' active' : ''}`}>
                    <div className="gallery-lightbox-backdrop" onClick={this.closeLb} />
                    {lbPic && (
                        <>
                            <button className="gallery-lb-close" onClick={this.closeLb}>✕</button>
                            <button className="gallery-lb-nav prev" onClick={this.prevLb}>‹</button>
                            <button className="gallery-lb-nav next" onClick={this.nextLb}>›</button>
                            <div className="gallery-lightbox-content">
                                <img src={lbPic.image_url || lbPic.thumbnail_url} alt={lbPic.caption || ''} />
                                {lbPic.caption && <div className="gallery-lb-caption">{lbPic.caption}</div>}
                                <div className="gallery-lb-counter">
                                    {(page - 1) * PAGE_SIZE + lbIndex + 1} / {total || pictures.length}
                                </div>
                                <div className="gallery-lb-actions">
                                    <button className="gallery-lb-action-btn" onClick={() => this.downloadImage(lbPic)}>
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                                        Tải xuống
                                    </button>
                                    <button className="gallery-lb-action-btn" onClick={() => this.shareImage(lbPic)}>
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
                                        Chia sẻ
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </div>

                {/* Toast */}
                {toast && <div className="alb-toast">{toast}</div>}

            </div>
        );
    }
}

export default AlbumDetail;
